import { Button, Card, Checkbox, Space, Switch, Table, Tag, Tooltip, Typography } from "antd";
import { CopyOutlined, WarningOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { dateFormat } from "../../../core/utils/date-utils";
import { copyToClipboard } from "../../../core/utils/clipboard-utils";
import { billingStatusColor, billingStatusKey } from "../../../enums/ReportBillingStatusEnum";

/**
 * Tek mükerrer grubu. Korunacak rapor işaretsiz gelir; grup anahtarı kapatılırsa gruptaki hiçbir
 * rapor işaretlenmez (kullanıcı "hepsini tut" diyebilir).
 *
 * Zincirleme uyarısı: yakınlık ARDIŞIK ölçüldüğü için grubun uçtan uca süresi eşiği aşabilir
 * (A–B 30dk, B–C 30dk → 60dk). Aşan gruplarda başlıkta uyarı gösterilir.
 */
export default function DuplicateGroupCard({ group, selectedIds, onToggleReport, onToggleGroup, threshold }) {
    const { t } = useTranslation();

    const markable = group.reports.filter(x => !x.isKeeper);
    const selectedCount = markable.filter(x => selectedIds.has(x.reportId)).length;
    const groupActive = selectedCount > 0;
    const chained = group.spanMinutes > threshold;

    // Kopyalanan küme SEÇİLİ satırlardır (işaretlenecek/silinecek olanlar); tablodaki checkbox
    // neyi kastediyorsa buton da onu kopyalar. Sıra tablo sırasıdır.
    const studyIds = group.reports
        .filter(x => selectedIds.has(x.reportId))
        .map(x => x.studyId)
        .filter(Boolean);

    const copyStudyIds = () => copyToClipboard(studyIds.join(','), {
        description: `${studyIds.length} ${t('Study Id')}`,
    });

    const columns = [
        {
            title: '',
            key: 'select',
            width: 44,
            render: (text, record) => (
                <Checkbox
                    checked={selectedIds.has(record.reportId)}
                    onChange={(e) => onToggleReport(record, e.target.checked)}
                />
            )
        },
        {
            title: '',
            key: 'keeper',
            width: 90,
            render: (text, record) => (record.isKeeper
                ? (
                    <Tooltip title={record.hasHl7 ? t('DUPLICATE_KEEPER_HL7') : t('DUPLICATE_KEEPER_EARLIEST')}>
                        <Tag color={'green'}>{t('Keeper')}</Tag>
                    </Tooltip>
                )
                : null)
        },
        { title: 'Id', dataIndex: 'reportId', key: 'reportId', width: 90 },
        {
            title: t('Study Id'),
            dataIndex: 'studyId',
            key: 'studyId',
            width: 110,
            // Tetkik UID yerine çekim id'si: kullanıcı bu değeri diğer ekranlara yapıştırıyor.
            // copyable.text açıkça veriliyor: id sayı olarak geliyor, antd string olmayan
            // çocuktan kopyalanacak değeri çıkaramıyor.
            render: (text) => (text
                ? <Typography.Text copyable={{ text: String(text) }}>{text}</Typography.Text>
                : '-')
        },
        {
            title: 'HL7',
            dataIndex: 'hasHl7',
            key: 'hasHl7',
            width: 60,
            render: (text) => (text ? <Tag color={'blue'}>HL7</Tag> : '-')
        },
        {
            title: t('Study Date'),
            dataIndex: 'studyDate',
            key: 'studyDate',
            width: 130,
            render: (text) => (text ? dateFormat(text) : '-')
        },
        {
            title: t('Report Date'),
            dataIndex: 'reportDate',
            key: 'reportDate',
            width: 130,
            render: (text) => (text ? dateFormat(text) : '-')
        },
        {
            title: t('Read By User'),
            dataIndex: 'readByUser',
            key: 'readByUser',
            width: 120,
            render: (text) => text || '-'
        },
        {
            title: t('Written By User'),
            dataIndex: 'writtenByUser',
            key: 'writtenByUser',
            width: 120,
            render: (text) => text || '-'
        },
        {
            title: t('Accession No'),
            dataIndex: 'accessionNumber',
            key: 'accessionNumber',
            width: 130,
            render: (text) => text || '-'
        },
        {
            title: t('Confirm Date'),
            dataIndex: 'confirmDate',
            key: 'confirmDate',
            width: 130,
            render: (text) => (text ? dateFormat(text) : '-')
        },
        {
            title: 'Hbys',
            dataIndex: 'isHbysTransfer',
            key: 'isHbysTransfer',
            width: 60,
            render: (text) => (text ? '✔' : '-')
        },
        {
            title: t('Billing Status'),
            dataIndex: 'billingStatus',
            key: 'billingStatus',
            width: 140,
            render: (text) => <Tag color={billingStatusColor(text)}>{t(billingStatusKey(text))}</Tag>
        },
    ];

    return (
        <Card
            size={'small'}
            style={{ marginBottom: 12 }}
            title={(
                <Space wrap size={4}>
                    <b>{group.patientName || '-'}</b>
                    <Typography.Text type={'secondary'}>{group.dicomPatientId || '-'}</Typography.Text>
                    <span>·</span>
                    <span>{group.requestName || '-'}</span>
                    <span>·</span>
                    <Typography.Text type={'secondary'}>{group.hospitalName || '-'}</Typography.Text>
                    <Tag>{group.reports.length}</Tag>
                    {chained && (
                        <Tooltip title={t('DUPLICATE_CHAIN_WARNING', { span: group.spanMinutes })}>
                            <Tag color={'orange'} icon={<WarningOutlined />}>{group.spanMinutes} dk</Tag>
                        </Tooltip>
                    )}
                </Space>
            )}
            extra={(
                <Space>
                    <Button
                        size={'small'}
                        icon={<CopyOutlined />}
                        disabled={studyIds.length === 0}
                        onClick={copyStudyIds}
                    >
                        {t('Copy Study Ids')}{studyIds.length > 0 ? ` (${studyIds.length})` : ''}
                    </Button>
                    <span style={{ fontSize: 12 }}>{t('Mark This Group')}</span>
                    <Switch
                        size={'small'}
                        checked={groupActive}
                        onChange={(checked) => onToggleGroup(group, checked)}
                    />
                </Space>
            )}
        >
            <Table
                size={'small'}
                rowKey={'reportId'}
                dataSource={group.reports}
                columns={columns}
                pagination={false}
                scroll={{ x: true }}
                rowClassName={(record) => (record.isKeeper ? 'duplicate-keeper-row' : '')}
            />
        </Card>
    )
}
